import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';
import SEOHead from '@/components/base/SEOHead';

export default function CategoriesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || categories[0].slug;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const idx = categories.findIndex((c) => c.slug === activeTab);
    setActiveIndex(idx >= 0 ? idx : 0);
  }, [activeTab]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeTab]);

  const activeCategory = categories[activeIndex];
  const categoryArticles = getArticlesByCategory(activeTab);

  const breadcrumbSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://readdy.ai' },
      { '@type': 'ListItem', position: 2, name: '风格分类' },
    ],
  }), []);

  const handleTabChange = (slug: string) => {
    setSearchParams({ tab: slug });
  };

  return (
    <>
    <SEOHead schema={breadcrumbSchema} />
    <main className="pt-20 md:pt-24">
      <nav className="bg-background-50 border-b border-background-200" aria-label="面包屑导航">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
          <ol className="flex items-center gap-2 text-xs text-foreground-500">
            <li><Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">首页</Link></li>
            <li><i className="ri-arrow-right-s-line text-xs"></i></li>
            <li><span className="text-foreground-800">风格分类</span></li>
          </ol>
        </div>
      </nav>

      <section className="relative py-16 md:py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20artistic%20fashion%20background%20with%20flowing%20fabric%20textures%20in%20warm%20burgundy%20and%20cream%20tones%2C%20soft%20ethereal%20lighting%2C%20minimalist%20editorial%20style%2C%20luxury%20fashion%20aesthetic&width=1920&height=500&seq=categories-hero&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/40"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 text-center">
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-4">
            风格<span className="text-accent-400">分类</span>
          </h1>
          <p className="text-background-200 text-sm md:text-base max-w-lg mx-auto">
            选择一种风格，释放你内心的那个她。
          </p>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-wrap items-center gap-2 py-6 border-b border-background-200">
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleTabChange(cat.slug)}
                className={`px-5 py-2 text-sm font-medium rounded-full transition-all duration-300 whitespace-nowrap ${
                  activeTab === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          <div className="py-8">
            <div className="mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                {activeCategory.name}
              </h2>
              <p className="text-foreground-600 text-sm leading-relaxed max-w-2xl">
                {activeCategory.description}
              </p>
            </div>

            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 pb-16">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/articles/${article.slug}`}
                    className="group block bg-background-100 rounded-lg overflow-hidden transition-all duration-300"
                  >
                    <div className="relative h-52 overflow-hidden">
                      <div className="absolute inset-0 w-full h-full">
                        <img
                          src={article.coverImage}
                          alt={article.title}
                          title={`${article.title} - 裙子下面是野兽`}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 text-xs text-foreground-500 mb-2">
                        <span>{article.author}</span>
                        <span>·</span>
                        <span>{article.date}</span>
                      </div>
                      <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-foreground-600 text-sm leading-relaxed line-clamp-2">
                        {article.excerpt}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-foreground-500 text-sm">该分类下暂无文章，敬请期待。</p>
              </div>
            )}
          </div>
        </div>
      </section>
    </main>
    </>
  );
}